// Picking things on the map, as in Gather: a single click (or tap) selects, a double one walks there (deck/input.js).
// Selected:
// - a person: their card in the top-right corner (public/greet.js pin), with 打招呼 and 走过去;
// - an object: a small panel in the same corner saying what it is and what can be done there: a seat (工位, 长凳,
//   坐垫, 会议圆桌, and the map editor's furniture with seats) 坐下 / 起身, and at a desk 我的工位 (public/desks.js: 申请 /
//   释放 / 装饰); a game table 加入游戏 / 观战; a game hut
//   加入游戏, a room 走进去 (a click anywhere on its floor or walls); a fishing spot at sea 钓鱼 (the only way into
//   钓鱼 mode: public/fishing.js) / 游过去; the gather flag 去集合;
// - nothing (open deck, or open sea: a double click swims there): whatever was selected is let go.
// Walking into a room (not a game hut: public/games.js shows its own card there) shows its card by itself, and walking
// out hides it again, unless something else was selected meanwhile.
// Each action walks there itself when it needs to (sit.js, gomoku.js, mover.travelTo). The selected thing is marked
// on the map (mark(): deck/scene.js draws a ring there). More kinds can be added with add(provider) (e.g. a whiteboard).
import { icon, label, put } from '/icons.js';
import { seatAtPoint, sittingOn } from '/seats.js';
import { findSpot } from '/fishing-spot.js';
import { t, tr } from '/i18n.js';

const CSS = `#pickCard{display:none;flex-direction:column;gap:6px;padding:8px 10px;position:relative;animation:greetPop .2s ease-out}
#pickCard .hd{display:flex;align-items:center;gap:6px;font-weight:700;padding-right:18px;min-width:0}
#pickCard .hd b{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
#pickCard .sub{font-size:12px;color:#666;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
#pickCard .sub:empty{display:none}
#pickCard .sched{font-size:12px;line-height:1.45;color:#1b1410;background:#fff;border:1px dashed #c9b994;border-radius:8px;padding:3px 7px;max-width:260px}
#pickCard .sched:empty{display:none} #pickCard .sched div{overflow:hidden;text-overflow:ellipsis;white-space:nowrap} #pickCard .sched .now{color:#b91c1c;font-weight:600}
#pickCard .row{display:flex;flex-wrap:wrap;gap:6px}
#pickCard .row button.alt{background:#fff}
#pickCard .tog{display:flex;flex-wrap:wrap;gap:4px;align-items:center;font-size:12px;color:#666}
#pickCard .tog:empty{display:none}
#pickCard .tog button{padding:3px 4px!important;line-height:0;background:#fff;opacity:.55}
#pickCard .tog button.on{background:#FDD03B;opacity:1}
#pickCard .x{position:absolute;right:4px;top:2px;border:0!important;background:none!important;padding:0 4px!important;font-size:16px;color:#666}`;
const SEAT_NAME = { desk: 'pick.seat.desk', meeting: 'pick.seat.meeting', bench: 'pick.seat.bench', cushion: 'pick.seat.cushion' }; // catalog keys
const HUTS = new Set(['game', 'spy', 'bonk']); // the game huts (public/games.js): walking in shows their game card
const REFRESH_MS = 400;
const CLICK_WALK = 'pick.tip.walk'; // what the hover tooltip says over a thing that has no verb of its own
const SWIM = 'pick.swim'; // otters swim in the water (deck/pose.js); there is no boat to see (p.boat only means afloat)

/**
 * @param {object} o
 * @param {object} o.MAP  @param {() => object|null} o.me  @param {() => Map} o.peers  @param {object} o.mover deck/move.js
 * @param {object} o.sit public/sit.js  @param {object} o.gomoku public/gomoku.js  @param {object} o.fishing public/fishing.js
 * @param {object} o.greet public/greet.js (pin)  @param {() => object|null} o.rally  @param {() => void} o.goRally
 * @param {(x: number, y: number) => object|null} o.zoneAt  @param {(text: string) => void} o.toast
 * @param {() => boolean} [o.blocked] the map is covered (map editor, full meeting view): nothing stays selected
 * @param {object} [o.desks] public/desks.js (a desk's 我的工位 actions)
 */
export function initPick(o) {
  const { MAP, me, peers, mover } = o;
  const st = document.createElement('style'); st.textContent = CSS; document.head.appendChild(st);
  const card = document.createElement('div'); card.id = 'pickCard';
  card.innerHTML = '<button class="x">×</button><div class="hd"></div><div class="sub"></div><div class="sched"></div><div class="row"></div><div class="tog"></div>';
  (document.getElementById('greetbox') ?? document.body).prepend(card);
  const q = (s) => card.querySelector(s);
  tr(q('.x'), null, null, { title: 'pick.close' });
  q('.x').onclick = () => clear();

  let cur = null; // {key, view(), mark(), person?}
  let arrive = null; // {until, ready(), go()}: something to do once the walk there is over (钓鱼)
  const providers = [];

  // ---- what's at a point
  /** Someone drawn at world point (wx, wy) tiles (their body, as drawn), front-most first; not me. */
  function personAt(wx, wy) {
    let hit = null;
    for (const p of peers().values()) {
      const x = p.drawX ?? p.x, y = p.drawY ?? p.y;
      if (wx >= x + 0.05 && wx < x + 0.95 && wy >= y - 0.55 && wy < y + 1.0 && (!hit || y > (hit.drawY ?? hit.y))) hit = p;
    }
    return hit;
  }
  const sitter = (s) => { for (const p of peers().values()) if (p.seat === s.id && sittingOn(MAP, p)) return p; return null; };
  const walkNear = (tx, ty) => mover.travelTo(tx, ty, false, null);

  function seatTarget(s) {
    return {
      key: 'seat:' + s.id,
      mark: () => ({ x: s.fx, y: s.fy + 0.1, r: 0.55 }),
      view() {
        const m = me(), mine = !!m && sittingOn(MAP, m)?.id === s.id, who = !mine && sitter(s);
        const title = s.name || (SEAT_NAME[s.kind] ? t(SEAT_NAME[s.kind]) : t('pick.seat.default'));
        const sub = mine ? t('pick.seat.mine') : who ? t('pick.seat.taken', { name: who.name }) : s.kind === 'meeting' ? t('pick.seat.freeMeeting') : s.kind === 'desk' ? t('pick.seat.freeDesk') : t('pick.seat.free');
        const actions = mine ? [{ icon: 'otter', text: t('pick.seat.stand'), go: () => o.sit.stand() }]
          : who ? [{ icon: 'pin', text: t('pick.walk'), go: () => walkNear(Math.floor(s.fx), Math.floor(s.fy)) }]
          : [{ icon: s.kind === 'desk' ? 'desk' : 'otter', text: t('pick.seat.sit'), go: () => o.sit.goSit(s) }, { icon: 'pin', text: t('pick.walk'), alt: true, go: () => walkNear(Math.floor(s.fx), Math.floor(s.fy)) }];
        // 我的工位 (public/desks.js): whose desk it is, claiming / releasing it, its owner's decorations
        const d = s.kind === 'desk' && o.desks ? o.desks.panel(s) : null;
        if (d) return { icon: 'desk', title: d.title ?? title, sub: d.note ? t('pick.seat.note', { sub: !mine && !who ? t('pick.seat.free') : sub, note: d.note }) : sub, actions: [...actions.filter((a) => !a.alt), ...d.actions, ...actions.filter((a) => a.alt)], toggles: d.toggles, seat: s };
        return { icon: s.kind === 'desk' ? 'desk' : s.kind === 'meeting' ? 'people' : 'otter', title, sub, actions, seat: s };
      },
      tip() { const m = me(); return m && sittingOn(MAP, m)?.id === s.id ? t('pick.tip.stand') : sitter(s) ? t(CLICK_WALK) : t('pick.tip.sit'); },
    };
  }
  function tableTarget(t0) {
    return {
      key: 'table:' + t0.id,
      mark: () => ({ x: t0.x + 0.5, y: t0.y + 0.5, r: 1.5 }),
      view() {
        const tb = o.gomoku.tables().find((x) => x.id === t0.id) ?? t0, cur = o.gomoku.current?.(), here = cur?.table === tb.id;
        const myId = me()?.gid ?? me()?.id, mine = here && cur.seats?.some((s) => s?.id === myId), full = here ? !!(cur.seats?.[0] && cur.seats?.[1]) : (tb.seated ?? 0) >= 2;
        const sub = mine ? t('pick.table.mine') : tb.busy ? t('pick.table.busy', { game: tb.kindName ?? t('pick.table.game') }) : tb.seated ? t('pick.table.waiting', { n: tb.seated }) : t('pick.table.empty');
        const actions = mine ? [] : full ? [{ icon: 'people', text: t('pick.table.watch'), go: () => walkNear(Math.round(tb.x), tb.y) }]
          : [{ icon: 'stones', text: t('pick.join'), go: () => o.gomoku.tableClick(tb.x, tb.y) || walkNear(Math.round(tb.x), tb.y) }, { icon: 'pin', text: t('pick.walk'), alt: true, go: () => walkNear(Math.round(tb.x), tb.y) }];
        return { icon: 'stones', title: tb.name || t('pick.table.name'), sub, actions };
      },
      tip() { // as a double click goes (gomoku.js tableClick): sit down, or walk up to watch a full table
        const tb = o.gomoku.tables().find((x) => x.id === t0.id) ?? t0, cur = o.gomoku.current?.(), here = cur?.table === tb.id;
        const myId = me()?.gid ?? me()?.id;
        if (cur?.seats?.some((s) => s?.id === myId)) return t('pick.tip.view'); // at this table, or at another one (gomoku.js: it says so)
        return (here ? !!(cur.seats?.[0] && cur.seats?.[1]) : (tb.seated ?? 0) >= 2) ? t('pick.tip.watch') : t('pick.tip.play');
      },
    };
  }
  /** A free tile in zone z nearest its middle, to walk to. */
  function insideZone(z) {
    let best = null, bd = Infinity;
    const cx = z.x + z.w / 2, cy = z.y + z.h / 2;
    for (let y = Math.floor(z.y); y < z.y + z.h; y++) for (let x = Math.floor(z.x); x < z.x + z.w; x++) {
      const d = Math.hypot(x + 0.5 - cx, y + 0.5 - cy);
      if (d < bd && mover.footOk(x, y) && o.zoneAt(x, y - 0.3)?.id === z.id) { bd = d; best = [x, y]; }
    }
    return best;
  }
  function zoneTarget(z) {
    const hut = HUTS.has(z.id);
    return {
      key: 'zone:' + z.id,
      mark: () => ({ box: { x: z.x, y: z.y, w: z.w, h: z.h } }),
      view() {
        const all = [me(), ...peers().values()].filter((p) => p && o.zoneAt(p.x, p.y)?.id === z.id), inHere = !!me() && all.includes(me());
        const count = t('pick.room.count', { n: all.length }), sub = z.meeting ? t('pick.room.meeting', { count }) : count;
        const walk = () => { const at = insideZone(z); if (!at) return o.toast(t('pick.room.noWay')); mover.travelTo(at[0], at[1], true, [at[0], at[1] - 0.3]); }; // (enterZone below: the same)
        const actions = inHere ? [] : [{ icon: hut ? 'gamepad' : 'door', text: hut ? t('pick.join') : t('pick.room.enter'), go: walk }];
        // a 会议室's bookings today (日历, public/calendar.js): what's on now and next, and 约这个会议室
        const cal = z.meeting ? o.roomSchedule?.(z.id) : null;
        if (cal?.book) actions.push({ icon: 'timer', text: t('pick.room.book'), alt: true, go: cal.book });
        return { icon: hut ? 'gamepad' : z.meeting ? 'people' : 'door', title: z.name, sub: inHere ? t('pick.room.here', { sub }) : sub, actions, sched: cal?.lines ?? [] };
      },
      tip: () => t(CLICK_WALK),
    };
  }
  /** Where to stand to fish at sea tile (tx, ty): a deck tile nearby facing it, nearest me. {x, y, dir} or null. */
  function fishFrom(tx, ty) {
    const m = me(); let best = null, bd = Infinity;
    for (let y = ty - 3; y <= ty + 3; y++) for (let x = tx - 3; x <= tx + 3; x++) {
      if (!mover.footOk(x, y)) continue;
      const px = x, py = y - 0.3;
      for (let dir = 0; dir < 4; dir++) {
        const s = findSpot(MAP, px, py, dir);
        if (!s || s.dir !== dir || Math.hypot(s.bx - tx, s.by - ty) > 1.5) continue;
        const d = Math.hypot(s.bx - tx, s.by - ty) * 3 + (m ? Math.hypot(px - m.x, py - m.y) * 0.1 : 0);
        if (d < bd) { bd = d; best = { x: px, y: py, dir, tx: x, ty: y }; }
      }
    }
    return best;
  }
  /** A sea tile: a fishing spot has a card (钓鱼, 游过去); open sea has none, only the hover tip (view: null). */
  function seaTarget(tx, ty) {
    const spot = fishFrom(tx, ty);
    return {
      key: `sea:${tx},${ty}`, sea: true, spot: !!spot,
      mark: () => ({ x: tx + 0.5, y: ty + 0.5, r: 0.6 }),
      view: spot ? () => {
        const s = fishFrom(tx, ty) ?? spot;
        return { icon: 'sea_wave', title: t('pick.fish.title'), sub: t('pick.fish.sub'),
          actions: [{ icon: 'rod', text: t('pick.fish.go'), go: () => goFish(s) }, { icon: 'sea_wave', text: t(SWIM), alt: true, go: () => mover.clickAt(tx + 0.5, ty + 0.5) }] };
      } : null,
      tip: () => t(spot ? 'pick.tip.swimSpot' : 'pick.tip.swim'),
    };
  }
  let seaMemo = null; // the last sea tile looked at (hover runs on every mouse move; fishFrom looks round 7 x 7 tiles)
  const seaAt = (tx, ty) => { const k = tx + ',' + ty; if (seaMemo?.k !== k || Date.now() - seaMemo.t > 2000) seaMemo = { k, t: Date.now(), v: seaTarget(tx, ty) }; return seaMemo.v; };
  async function goFish(spot) {
    const m = me(); if (!m) return;
    const there = () => { const p = me(); return !!p && !p.boat && !p.moving && !mover.planning() && !mover.path() && !mover.plan().length && Math.hypot(p.x - spot.x, p.y - spot.y) < 0.35; };
    // there: face the water and open 钓鱼 mode (it has the cast button; the panel here lets go)
    const go = () => { const p = me(); if (!p) return; if (!o.fishing.busy?.()) { p.dir = spot.dir; p.dir8 = spot.dir * 2; p.dir16 = spot.dir * 4; } o.fishing.open(); clear(); };
    if (there()) return go();
    if (!(await mover.travelTo(spot.tx, spot.ty, true, [spot.x, spot.y]))) return;
    arrive = { until: Date.now() + 60000, ready: there, go };
  }
  function rallyTarget() {
    return {
      key: 'rally',
      mark: () => { const r = o.rally(); return r ? { x: r.x + 0.5, y: r.y + 0.9, r: 0.6 } : null; },
      view() { const r = o.rally(); return { icon: 'flag', title: t('pick.rally.title'), sub: r?.by ? t('pick.rally.by', { name: r.by }) : '', actions: [{ icon: 'flag', text: t('pick.rally.go'), go: o.goRally }] }; },
      tip: () => t('pick.tip.rally'),
    };
  }
  function personTarget(p) {
    return { key: 'person:' + p.id, person: p, mark: () => { const q = peers().get(p.id); return q ? { x: (q.drawX ?? q.x) + 0.5, y: (q.drawY ?? q.y) + 0.95, r: 0.5 } : null; }, view: null,
      tip: () => t(CLICK_WALK) };
  }

  /** What's at world point (wx, wy): a target, or null (open deck). click: a single click (it also takes a room's
   *  floor; hovering there stays floor, so the double-click-to-walk outline and hint keep working in rooms). */
  function targetAt(wx, wy, click = false) {
    const tx = Math.floor(wx), ty = Math.floor(wy);
    const p = personAt(wx, wy); if (p) return personTarget(p);
    for (const f of providers) { const t = f(wx, wy, tx, ty); if (t) return t; }
    const r = o.rally(); if (r && Math.abs(tx - r.x) <= 1 && Math.abs(ty - r.y) <= 2) return rallyTarget();
    const s = seatAtPoint(MAP, wx, wy); if (s) return seatTarget(s);
    const t = (MAP.tables ?? []).find((x) => Math.abs(tx - x.x) <= 1 && ty === x.y); if (t) return tableTarget(t);
    const free = mover.footOk(tx, ty);
    // a room: its walls and roof (and its floor: on a click, or any time for a game hut)
    const inside = (z) => tx >= z.x && tx < z.x + z.w && ty >= z.y && ty < z.y + z.h;
    const z = MAP.zones.find((z) => tx >= z.x - 1 && tx < z.x + z.w + 1 && ty >= z.y - 1 && ty < z.y + z.h + 1 && (!free || (click || HUTS.has(z.id)) && inside(z)));
    if (z) return zoneTarget(z);
    if (!free && mover.water(tx, ty)) return seaAt(tx, ty);
    return null;
  }

  // ---- showing it
  function render() {
    if (!cur || cur.person || !cur.view) { card.style.display = 'none'; return; }
    const v = cur.view();
    card.style.display = 'flex';
    const hd = q('.hd'), k = v.icon + '|' + v.title;
    if (hd.dataset.k !== k) { hd.dataset.k = k; const b = document.createElement('b'); b.textContent = v.title; b.title = v.title; hd.replaceChildren(icon(v.icon), b); }
    if (q('.sub').textContent !== v.sub) q('.sub').textContent = v.sub;
    const sched = q('.sched'), ssig = (v.sched ?? []).map((l) => l.text + !!l.now).join('|');
    if (sched.dataset.k !== ssig) { sched.dataset.k = ssig; sched.replaceChildren(...(v.sched ?? []).map((l) => { const d = document.createElement('div'); d.textContent = l.text; if (l.now) d.className = 'now'; return d; })); }
    const row = q('.row'), sig = v.actions.map((a) => a.icon + a.text + !!a.alt + !!a.disabled).join('|');
    if (row.dataset.k !== sig) {
      row.dataset.k = sig;
      row.replaceChildren(...v.actions.map((a, i) => { const b = document.createElement('button'); b.dataset.i = i; if (a.alt) b.className = 'alt'; label(b, a.icon, a.text); b.disabled = !!a.disabled; if (a.title) b.title = a.title; return b; }));
    }
    for (const b of row.children) { const a = v.actions[+b.dataset.i]; b.onclick = () => { a.go(); setTimeout(render, 300); }; }
    // toggles (a desk's 装饰): small picture buttons (t.el, else the icon t.icon), lit when on
    const tog = q('.tog'), ts = v.toggles ?? [], tsig = t('pick.decor') + '|' + ts.map((x) => x.icon + !!x.on).join('|'); // (the label: redrawn on a language change)
    if (tog.dataset.k !== tsig) {
      tog.dataset.k = tsig;
      tog.replaceChildren(...(ts.length ? [document.createTextNode(t('pick.decor'))] : []), ...ts.map((t, i) => { const b = document.createElement('button'); b.dataset.i = i; b.className = t.on ? 'on' : ''; b.title = t.title; b.setAttribute('aria-label', t.title); b.setAttribute('aria-pressed', String(!!t.on)); b.append(t.el ? t.el() : icon(t.icon)); return b; }));
    }
    for (const b of tog.querySelectorAll('button')) { const t = ts[+b.dataset.i]; b.onclick = () => { t.go(); setTimeout(render, 300); }; }
  }
  function select(t) {
    if (cur?.person && t?.key !== cur.key) o.greet.pin(null);
    cur = t;
    if (t?.person) o.greet.pin(t.person);
    render();
  }
  function clear() { select(null); }

  // the room I'm in (not a game hut): its card shows by itself while nothing else is selected (auto: shown that way)
  let inRoom = null;
  function roomCard() {
    const m = me(), id = (m && !o.blocked?.() && o.zoneAt(m.x, m.y)?.id) || null;
    if (id === inRoom) return;
    const was = inRoom; inRoom = id;
    if (was && cur?.auto && cur.key === 'zone:' + was) select(null); // walked out: hide it (unless something else was picked)
    if (id && HUTS.has(id) && cur?.key === 'zone:' + id) select(null); // in a game hut its own card shows (games.js), not two
    const z = id && !HUTS.has(id) && MAP.zones.find((q) => q.id === id);
    if (z && cur?.key === 'zone:' + id) cur.auto = true; // picked before walking in (走进去, a double click's first click): it goes when I walk out too
    else if (z && (!cur || cur.auto)) select({ ...zoneTarget(z), auto: true });
  }
  setInterval(() => {
    if (cur && o.blocked?.()) select(null); // the map editor or the full meeting view took over
    roomCard();
    if (cur?.person && !peers().get(cur.person.id)) select(null); // they left
    else if (cur && !cur.person) render();
    if (arrive) {
      const m = me();
      if (!m || Date.now() > arrive.until || mover.keys?.size) arrive = null;
      else if (arrive.ready()) { const a = arrive; arrive = null; a.go(); }
      else if (!m.moving && !mover.planning() && !mover.path() && !mover.plan().length) arrive = null; // stopped somewhere else
    }
  }, REFRESH_MS / 2);
  addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || !cur || e.defaultPrevented) return;
    const a = document.activeElement; if (a && (a.matches?.('input, textarea, select') || a.isContentEditable)) return;
    e.preventDefault(); clear(); // handled: one Esc lets go of the card, 钓鱼 mode (fishing-mode.js) stays open
  });

  return {
    /** A single click / tap at world point (wx, wy): select what's there (or let go). Returns it (null: open deck). */
    click(wx, wy) {
      let t = targetAt(wx, wy, true); if (t && !t.person && !t.view) t = null; // open sea: nothing to show
      if (inRoom && HUTS.has(inRoom) && t?.key === 'zone:' + inRoom) t = null; // in a game hut its own card shows (games.js)
      if (t && cur?.auto && t.key === cur.key) return cur; // the room I'm in, shown already: it still goes when I walk out
      select(t); return t;
    },
    /** What the mouse is over at world point (wx, wy), for the hover outline and tooltip (deck/input.js):
     *  {key, kind: 'thing', tip, mark()} (as a target's mark), or {key, kind: 'floor' | 'wall', tile: {x, y}}. */
    hover(wx, wy) {
      const th = targetAt(wx, wy);
      if (th) return { key: th.key, kind: 'thing', tip: th.tip?.() ?? t(CLICK_WALK), mark: th.mark, sea: !!th.sea };
      const tx = Math.floor(wx), ty = Math.floor(wy), ok = mover.footOk(tx, ty) || (!!me()?.boat && mover.water(tx, ty));
      return { key: `tile:${tx},${ty}`, kind: ok ? 'floor' : 'wall', tile: { x: tx, y: ty } };
    },
    /** Let go of whatever is selected (the person card's ×, Esc). */
    clear,
    /** The map changed (map editor): let go, and show the room I'm in again (its card may have moved or gone). */
    mapChanged() { inRoom = null; clear(); },
    /** Something else to pick: provider(wx, wy, tx, ty) returns a target {key, mark(), view()} or null. */
    add(provider) { providers.push(provider); },
    /** The selected thing on the map: {x, y, r} (a ring at its foot, tiles) or {box: {x, y, w, h}}, or null. */
    mark: () => cur?.mark?.() ?? null,
    selected: () => cur,
    /** A walk started by hand (keys, a double click): forget the 钓鱼 waiting at the end of the walk. */
    walked() { arrive = null; },
    /** the panel's current view (for an agent or a test harness looking at the page) */
    view: () => (cur && !cur.person && cur.view ? cur.view() : null),
    seatTarget,
    /** Walk (or sail) into room z, as its card's 走进去 does (public/rooms.js 进去). False: no way in (toasted). */
    async enterZone(z) { const at = insideZone(z); if (!at) { o.toast(t('pick.room.noWay')); return false; } return mover.travelTo(at[0], at[1], true, [at[0], at[1] - 0.3]); },
  };
}
